import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import * as moment from "moment";
import {
    Document,
    FalconNavigatorHeader,
    ICustomerPortalService,
    Policy,
    productHelper,
    ProductsEnum,
    Types,
    CAR_MTA_CONFIRMATION_PACK,
    CAR_WELCOME_PACK,
    CAR_WOLF_CHARGEBACK_CANCELLATION,
    CAR_WOLF_CHARGEBACK_COVER,
    CAR_WOLF_CHARGEBACK_NO_COVER,
    FREETEXT_DOCUMENT,
    CAR_WOLF_CANCELLATION_EMAIL_TITLE,
    Renewal,
    CAR_WOLF_RENEWAL_INVITE_PACK,
    POLICY_STATUS_PENDING_RENEWAL,
    CAR_WOLF_RENEWAL_ACCEPTANCE,
    CAR_WOLF_RENEWAL_CLAIMS_IMPACT,
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {finalize, map} from "rxjs/operators";
import {RouteComponentProps} from "react-router-dom";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {downloadDocument, trimFileExtension} from "../../../../../CommonFunctions";
import {PostRequestedDocumentsButton} from "./PostRequestedDocumentsButton";
import {mapDocumentTitles} from "../../../../../utils/DataMappers";
import {AnnualHomeDocumentListModal} from "@falcon/plugin-falcon-journey";
import { FalconAccordion } from "../../../../../components/FalconAccordion";

interface IDocumentsPageParams {
    policyNumber: string;
}

interface IDocumentsPageProps extends RouteComponentProps<IDocumentsPageParams> {
    location: any;
}

interface IState {
    policies?: Policy[];
    archiveDocuments: Document[];
    direction: string;
    active: boolean;
    isWhite: boolean;
    isDocumentDownloaded: boolean;
    day: number;
    showHistory: boolean;
    homeCurrentTransactionalDocuments: Document[];
    receiveByPostChecked: boolean | null;
    showLoading: boolean;
    coverTier: string;
    renewalProposal?: Renewal;
    renewalDocuments: Document[];
}

@authorize
export class AnnualHomeDocumentsPage extends React.Component<IDocumentsPageProps, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly FILE_PATH: string = "path";
    private readonly FILE_NAME: string = "fileName";
    private readonly FILE_ID: string = "id";
    private readonly FILE_LABEL: string = "label";

    constructor(props: Readonly<IDocumentsPageProps>) {
        super(props);
        this.state = {
            direction: 'asc',
            archiveDocuments: [],
            active: false,
            isWhite: true,
            day: 0,
            showHistory: false,
            isDocumentDownloaded: true,
            homeCurrentTransactionalDocuments: [],
            receiveByPostChecked: null,
            showLoading: true,
            coverTier: '',
            renewalDocuments: []
        };

        this.sortByDate = this.sortByDate.bind(this);
        this.renderDocumentsByPost = this.renderDocumentsByPost.bind(this);
        this.createPDFMap = this.createPDFMap.bind(this);
        this.getDocuments = this.getDocuments.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy documents");

        this.customerPortalService
            .getPolicies()
            .pipe(map((policies: Policy[]) => policies.filter((policy: Policy) => !policy.endDate || moment(policy.endDate).isSameOrAfter(moment().startOf("day")))))
            .subscribe((activePolicies: Policy[]) => {
                this.customerPortalService
                    .getExpiredPolicies(ProductsEnum.HOME).pipe(finalize(() => {
                        this.setState({showLoading: false});
                    }))
                    .subscribe(expiredPolicies => {

                        const filteredActivePolicies: Policy[] = activePolicies
                            .filter((policy: Policy) => this.filterPolicyMatchedAndNotFutureCancelled(policy))
                            .sort((a, b) => moment(b.endDate).diff(a.endDate));

                        const policies = [...(filteredActivePolicies || []), ...(expiredPolicies || [])];

                        this.setState({
                            policies,
                            coverTier: policies[0].interaction.coverTier
                        });

                        this.getDocuments(activePolicies[0], policies);
                    });
            })
    }

    public render(): JSX.Element {
        const hasPendingRenewal: boolean = this.state.renewalProposal != null && this.state.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;

        return (
            <React.Fragment>
                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back={this.state.policies && `/dashboard/annualHome/${this.state.policies[0].policyNumber}`}
                        title="Documents"
                    />
                    {!this.state.showLoading && <div className="documents-page">
                        {!hasPendingRenewal && this.renderDocumentsPanel("Policy documents", this.state.homeCurrentTransactionalDocuments)}
                        {hasPendingRenewal && this.renderDocumentsPanelWithAccordion("Renewal policy documents", true, this.state.renewalDocuments, this.state.renewalProposal?.annualHomeFixedAddons)}
                        {hasPendingRenewal && this.renderDocumentsPanelWithAccordion("Existing policy documents", false, this.state.homeCurrentTransactionalDocuments, this.state.policies?.[0].interaction.annualHomeFixedAddons)}
                        {this.renderDocumentsArchivePanel()}
                        {this.renderDocumentsByPost()}
                    </div>}
                    {this.state.policies && this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualHome/${this.state.policies[0].policyNumber}`)}
                </div>
                <Overlay show={this.state.showLoading}/>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderDocumentsPanel(title: string, documents: Document[]) {
        return (
            <div className="widget documents-widget active-policy-documents-widget panel">
                <p className="active-policy-documents-title font-weight-bold text-center fs-18">{title}</p>
                <ul className="list-unstyled">
                    {this.renderTransactionalFiles(documents)}
                    <AnnualHomeDocumentListModal
                        policyDate={this.state.policies ? new Date(this.state.policies[0].startDate) : new Date()}
                        coverTier={this.state.renewalProposal?.coverTier ?? this.state.coverTier}
                        withTitle={false}
                        annualHomeFixedAddons={this.state.policies?.[0].interaction.annualHomeFixedAddons}
                    />
                </ul>
            </div>
        );
    }

    private renderDocumentsPanelWithAccordion(title: string, isShown: boolean, documents: Document[], annualHomeFixedAddons?: any): React.JSX.Element {
        if (this.state.policies) {
            return (
                <div className="widget documents-widget panel existing-documents-widget-pending-renewal">
                    <ul className="list-unstyled">
                        <FalconAccordion
                            togglePositionTop={true}
                            isShown={isShown}
                            toggleShown={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/>
                                <span className="text-center fs-18">{title}</span>
                                <span className="toggle-icon collapse-icon"/></div>}
                            toggleHidden={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded"><span/>
                                <span className="text-center fs-18">{title}</span>
                                <span className="toggle-icon expand-icon"/></div>}>
                            {this.renderTransactionalFiles(documents)}
                            <AnnualHomeDocumentListModal
                                policyDate={this.state.policies ? new Date(this.state.policies[0].startDate) : new Date()}
                                coverTier={this.state.renewalProposal?.coverTier ?? this.state.coverTier}
                                withTitle={false}
                                {...(annualHomeFixedAddons && { annualHomeFixedAddons })}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            )
        } else {
            return <></>
        }
    }

    private renderDocumentsArchivePanel(): React.JSX.Element {
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            this.state.direction === 'asc' ?
                this.state.archiveDocuments.sort((a, b) => moment(a.date).diff(b.date)) :
                this.state.archiveDocuments.sort((a, b) => moment(b.date).diff(a.date));

                return (
                    <div
                        className="widget documents-widget panel documents-archive">
                        <p className={"documents-archive-title font-weight-bold text-center"}>Documents archive</p>
                        <table>
                            <tbody>
                            {this.renderTableHeader()}
                            {this.renderArchivedDocumentsByDate()}
                            </tbody>
                        </table>
                    </div>
                );
        } else {
            return <></>;
        }
    }

    private renderDocumentsByPost() {
        const descArchiveDocuments: Document[] = [...this.state.archiveDocuments].sort((a, b) => moment(b.date).diff(a.date));
        const printableDocuments: Document[] = this.state.homeCurrentTransactionalDocuments.concat(descArchiveDocuments);

        return (
            <div className="receive-by-post">
                <p className="receive-by-post-title font-weight-bold fs-18">Receive my policy documents by post</p>
                <p className="receive-by-post-description">We've gone paperless; we care about the environment and it's better for everyone! But if you wish,
                    you can request below to have your documents posted to you to the address listed on your policy</p>
                {this.state.policies &&
                    <PostRequestedDocumentsButton policy={this.state.policies[0]}
                                                  allDocuments={printableDocuments}/>}
            </div>
        )
    }

    private renderTransactionalFiles(homeTransactionalDocuments: Document[]) {
        return homeTransactionalDocuments.map((document: Document, index: number) => (
            <li className="media" key={index}>
                <div className="media-body">
                    <div className="row height-0">
                        <div className="col-1"/>
                        <div className="col-11">
                            <div className="dashed-10 ml-3 mr-3"/>
                        </div>
                    </div>
                    <a className="document-download-link no-underline" onClick={this.downloadDocument(document)}
                       id={trimFileExtension(document.name)}>
                        <img className="pdf-icon active-icon" alt="pdf icon" src="/img/pdf-icon.png"/>
                        <h5 className={"pt-2 document-name"}>{document.title}</h5>
                    </a>
                </div>
            </li>
        ));
    }

    private filterPolicyMatchedAndNotFutureCancelled(policy: Policy): boolean {
        const futureCancelledPolicyBeforeInception: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSame(moment.utc(policy.startDate, moment.ISO_8601));
        const matchedPolicy: boolean = policy != null && policy.policyNumber === this.props.match.params.policyNumber;
        return !futureCancelledPolicyBeforeInception && matchedPolicy;
    }

    private downloadDocument = (doc: Document) => (event: any): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContents: ArrayBuffer) => {
                if (documentContents) {
                    downloadDocument(documentContents, doc.name);
                }
                this.setState({isDocumentDownloaded: true});
            }
        )
    }

    private renderTableHeader(): React.JSX.Element {
        return (
            <React.Fragment>
                <tr>
                    <th className={"pb-3 pl-3"}>
                        <span className="documents-archive-table-header muli-semi-bold-16">Date</span>
                        <span onClick={this.sortByDate} className={`${this.state.active &&
                        (this.state.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down")}
                            ${!this.state.active && "fas fa-sort"} table-sort-arrow`}/>
                    </th>
                </tr>
            </React.Fragment>
        )
    }

    private renderArchivedDocumentsByDate(): React.JSX.Element[] | string {
        let documents: React.JSX.Element[];
        let day: number = 0;
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            let previousDate: string | null = "";
            let isWhite: boolean = true;
            documents = this.state.archiveDocuments.map((document, index) => {
                const {date, title, name} = document;
                if (PluginTemplates.convertDate(date, "DD MMMM YYYY") === previousDate) {
                    const backGround: string = isWhite ? "" : "light-blue-background";
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <tr key={index}
                            className={`${backGround} ${history} ${this.state.showHistory ? "display-block" : ""}`}>
                            <td id="document-date" className="muli-semi-bold-16">
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"}
                                        onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page"
                                       onClick={this.downloadDocument(document)} id = {name? trimFileExtension(name) : index.toString()}>
                                        <img className="active-policy-download-icon" alt="Document download icon"
                                             src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                } else {
                    previousDate = PluginTemplates.convertDate(date, "DD MMMM YYYY");
                    isWhite = !isWhite;
                    day += 1;
                    const backGround: string = isWhite ? "" : "light-blue-background";
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <tr key={index}
                            className={`${backGround} ${history} ${this.state.showHistory ? "display-block" : ""}`}>
                            <td id="document-date" className="muli-semi-bold-16 pt-3">
                                <span
                                    className="font-weight-extra-light fs-14 ml-2">{PluginTemplates.convertDate(date, "DD MMMM YYYY")}</span>
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"}
                                        onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page"
                                       onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon"
                                             src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                }

            });
        } else {
            return "";
        }
        if (day !== this.state.day) {
            this.setState({day});
        }

        return documents;
    }

    private sortByDate(): void {
        this.setState({
            direction: this.state.direction === 'asc' ? 'desc' : 'asc',
            active: true
        });
    }

    private getDocuments(activePolicy: Policy, policies: Policy[]): void {
        this.customerPortalService.getAllDocuments().subscribe(
            (documents: Document[]) => {
                this.customerPortalService.getRenewalProposal(activePolicy.policyNumber).subscribe((renewalProposal: Renewal) => {
                    this.setState({renewalProposal});
                    this.fetchDocuments(policies, renewalProposal, documents);
                });
            }
        );
    }

    private fetchDocuments = (policies?: Policy[], renewalProposal?: Renewal, documents?: Document[]) => {
        const homeCurrentTransactionalDocuments: Document[] = [];
        const archiveDocuments: Document[] = [];
        let renewalDocuments: Document[] = [];

        if (policies && policies.length > 0 && documents) {
            policies.forEach((policy: Policy) => {

                const policyRenewalDocs: Document[] = this.fetchRenewalDocuments(documents, renewalProposal);
                renewalDocuments = renewalDocuments.concat(mapDocumentTitles(policyRenewalDocs, policy));

                const allPolicyDocs = documents.filter(doc => {
                    return doc.tags.filter(d => d === ("agreement_" + policy.policyNumber)).length > 0
                });

                const currentPolicyVersionDocs = allPolicyDocs.filter(doc => {
                    return doc.tags.filter(d => d === ("agreement_version_" + policy.interaction.agreementVersion)).length > 0
                });

                const documentsToBeListed = currentPolicyVersionDocs.filter(doc =>
                    doc.name.includes(CAR_WELCOME_PACK)
                    || doc.name.includes(CAR_MTA_CONFIRMATION_PACK)
                    || doc.name.includes(CAR_WOLF_RENEWAL_ACCEPTANCE)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_COVER)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_NO_COVER)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_CANCELLATION)
                    || doc.name.includes(CAR_WOLF_CANCELLATION_EMAIL_TITLE));

                const mappedCurrentDocuments = mapDocumentTitles(documentsToBeListed, policy);
                if (productHelper.includesAnnualHomeProduct([policy])) {
                    homeCurrentTransactionalDocuments.push(...mappedCurrentDocuments);
                }

                const documentsToBeListedWithoutAgreementVersion = allPolicyDocs.filter(doc =>
                    (doc.name.includes(CAR_WELCOME_PACK)
                    || doc.name.includes(CAR_MTA_CONFIRMATION_PACK)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_COVER)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_NO_COVER)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_CANCELLATION)
                    || doc.name.includes(CAR_WOLF_CANCELLATION_EMAIL_TITLE)
                    || doc.name.includes(FREETEXT_DOCUMENT)) && !doc.tags.includes(("agreement_version_" + policy.interaction.agreementVersion))
                );

                if (productHelper.includesAnnualHomeProduct([policy])) {
                    archiveDocuments.push(...mapDocumentTitles(documentsToBeListedWithoutAgreementVersion.reverse(), policy));
                }
            });
        }

        this.setState({homeCurrentTransactionalDocuments, archiveDocuments, renewalDocuments});
    };

    private fetchRenewalDocuments(documents: Document[], renewalProposal?: Renewal): Document[] {
        const returnDocs: Document[] = [];

        if (renewalProposal) {
            const renewalDocs: Document[] = documents.filter(doc => {
                return doc.tags.filter(d => d === (`renewal_${renewalProposal._id}`)).length > 0;
            });

            const invitePdfs: Document[] = renewalDocs.filter(doc =>
                doc.name.includes(CAR_WOLF_RENEWAL_INVITE_PACK)
                || doc.name.includes(CAR_WOLF_RENEWAL_CLAIMS_IMPACT));

            if (invitePdfs.length > 0) {
                const latestInvitePdf: Document = [...invitePdfs].sort((a, b) => moment(b.date).diff(moment(a.date)))[0];
                returnDocs.push(latestInvitePdf);
            }
        }

        return returnDocs;
    }


    private createPDFMap(path: string, fileName: string, id: string, label: string): Map<string, string> {
        return new Map()
            .set(this.FILE_PATH, path)
            .set(this.FILE_NAME, fileName)
            .set(this.FILE_ID, id)
            .set(this.FILE_LABEL, label);
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/home/documents/AnnualHomeDocumentsPage.tsx